import { faArrowLeft, faBuilding, faChalkboardUser, faDoorOpen, faEnvelope, faSchool, faTriangleExclamation, faUser, faUserGraduate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../Components/Header/Header";
import ModulesPicker from "../../Components/AccountForm/ModulesPicker";
import "../../Components/AccountForm/AccountForm.css";

const EditUser = ({ data, socket } : { data: any, socket?: any }) => {

  const [cookies] = useCookies();
  const en = cookies?.language === "en";
  const [error, setError] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const navigate = useNavigate();
  const { studentId } = useParams();
  if (!studentId || !data.userData?.admin) navigate(-1);

  const [inputs, setInputs] = useState({
        username: "",
        firstName: "",
        lastName: "",
        email: "",
        type: "",
        teacherId: "",
        school: "",
        department: "",
        grade: "",
        modules: [...JSON.parse(data.userData.modules)] || ["edit", "functional", "debug", "learn", "train", "test"]

  });

    useEffect(() => {
      (async () => {
        try {
          const options = {
            method: "GET",
            headers: {
              "Content-Type": "application/json"
            }
          };
          const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/get-users/${data.userData.userId}`, options);
          const response = await request.json();
          if (response.success) {
            const currentStudent = response.users.find((student: any) => student.userId === studentId);
            if (currentStudent) setInputs(currInputs => {
              return {...currInputs,
                username: currentStudent.username,
                firstName: currentStudent.firstName,
                lastName: currentStudent.lastName,
                email: currentStudent.email,
                teacherId: currentStudent?.teacherId || "",
                school: currentStudent?.school || "",
                department: currentStudent?.department || "",
                grade: currentStudent?.grade || "",
                type: currentStudent.type,
                modules: [...JSON.parse(currentStudent?.modules)] || ["edit", "functional", "debug", "learn", "train", "test"]
              };
          });
          } else console.log(response);
        } catch (e) {
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async (e : React.FormEvent) => {
      e.preventDefault();
      if (saving) return;

      setError("");

      if (!inputs.firstName.length || !inputs.lastName.length || !inputs.username.length || !inputs.email.length)
        return setError(en ? "Fill in the required fields!" : "Completati campurile obligatorii!");

      const emailValidation = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      if (!inputs.email.match(emailValidation))
        return setError(en ? "The email is not valid!" : "Email-ul nu este valid!");

      setSaving(true);

      try {
        const options = {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            username: inputs.username,
            firstName: inputs.firstName,
            lastName: inputs.lastName,
            email: inputs.email,
            type: inputs.type,
            active: null,
            teacherId: inputs.teacherId,
            department: inputs.department,
            school: inputs.school,
            grade: inputs.grade,
            userId: studentId,
            modules: inputs.modules,
            adminId: data.userData.userId
          })
        };

        const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-user`, options);
        const response = await request.json();
        if (response.success) {
          window.location.replace("/users");
        } else {
          setError(en ? response.message.en : response.message.ro);
          setSaving(false);
        }
      } catch (e) {
        setError(en ? "The server could not be reached." : "Serverul nu a putut fi contactat.");
        setSaving(false);
      }
    };

    const handleInputChange = (e : React.ChangeEvent<HTMLInputElement>) => {
      setInputs(currInputs => {
        return {
          ...currInputs,
          [e.target.name]: e.target.value
        };
      });
    };

    const toggleModule = (key: string) => {
      setInputs(currInputs => ({
        ...currInputs,
        modules: currInputs.modules.includes(key)
          ? currInputs.modules.filter((m: string) => m !== key)
          : [...currInputs.modules, key]
      }));
    };

    const field = (
      name: "firstName" | "lastName" | "username" | "email" | "school" | "grade" | "department",
      label: string, placeholder: string, icon: any, type = "text",
    ) => (
      <label className="acc-field">
        <span>{label}</span>
        <div className="acc-input">
          <FontAwesomeIcon icon={icon} />
          <input
            type={type}
            name={name}
            value={(inputs as any)[name]}
            onChange={handleInputChange}
            placeholder={placeholder}
          />
        </div>
      </label>
    );

    const isTeacher = inputs.type === "teacher";
    const fullName = `${inputs.firstName} ${inputs.lastName}`.trim();

    return (
        <div className="acc-page">
            <Header data={data} socket={socket} />

            <div className="acc-card">
                <form className="acc-form" onSubmit={save}>

                    <header className="acc-head">
                        <span className="acc-avatar">
                            <FontAwesomeIcon icon={isTeacher ? faChalkboardUser : faUserGraduate} />
                        </span>
                        <div className="acc-head-text">
                            <h2 className="acc-title">{en ? "Edit the user" : "Editează utilizatorul"}</h2>
                            <p className="acc-sub">
                                {fullName || (en ? "User" : "Utilizator")}
                                {inputs.username ? ` · @${inputs.username}` : ""}
                            </p>
                        </div>
                        {inputs.type ? (
                            <span className="acc-role">
                                {isTeacher ? (en ? "Teacher" : "Profesor") : (en ? "Student" : "Elev")}
                            </span>
                        ) : null}
                    </header>

                    <section className="acc-section">
                        <p className="acc-kicker">
                            <span className="acc-kicker-dot" />
                            {en ? "Account details" : "Datele contului"}
                        </p>

                        <div className="acc-grid">
                            {field("firstName", en ? "First name *" : "Nume *", en ? "First name" : "Nume", faUser)}
                            {field("lastName", en ? "Last name *" : "Prenume *", en ? "Last name" : "Prenume", faUser)}
                            {field("username", en ? "Username *" : "Nume de utilizator *", en ? "Username" : "Nume de utilizator", faUser)}
                            {field("email", "Email *", "email@exemplu.ro", faEnvelope, "email")}

                            {/* A teacher account belongs to a company, a student one to a school. */}
                            {field(
                                "school",
                                isTeacher ? (en ? "Company" : "Companie") : (en ? "School" : "Școală"),
                                isTeacher ? (en ? "Company" : "Companie") : (en ? "School" : "Școală"),
                                isTeacher ? faBuilding : faSchool,
                            )}

                            {isTeacher
                                ? field("department", en ? "Department" : "Departament", en ? "Department" : "Departament", faDoorOpen)
                                : field("grade", en ? "Grade" : "Clasă", en ? "Grade" : "Clasă", faDoorOpen)}
                        </div>
                    </section>

                    <section className="acc-section">
                        <p className="acc-kicker">
                            <span className="acc-kicker-dot" />
                            {en ? "Module access" : "Acces la module"}
                        </p>

                        <ModulesPicker value={inputs.modules} onToggle={toggleModule} en={en} />
                    </section>

                    {error.length ? (
                        <p className="acc-error">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            {error}
                        </p>
                    ) : null}

                    <div className="acc-actions">
                        <button type="submit" className="acc-btn" disabled={saving}>
                            {saving
                                ? (en ? "Saving…" : "Se salvează…")
                                : (en ? "Save changes" : "Salvează modificările")}
                        </button>
                        <button type="button" className="acc-btn ghost" onClick={() => navigate("/users")}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                            {en ? "Back to users" : "Înapoi la utilizatori"}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default EditUser;
